import { useEffect, useState } from "react";
import { Tabs } from "antd";
import { useLoader } from "../../Context/LoaderContext";
import { Organization } from "../../Entities/Organization";
import organizationService from "../../Service/organization.service";
import OrgDetails from "./components/OrgDetails";
import ThemeDetails from "./components/ThemeDetails";
import IntegrationDetails from "./components/IntegrationDetails";
import { useSearchParams } from "react-router-dom";
import OrganizationSettingsPageTitle from "./components/OrganizationSettingsPageTitle";
import Apps from "./components/Apps";
import AuthService from "../../Service/auth.service";
import useAccess from "../../Hooks/useAccess";
import "./index.scss";

const OrganizationSettings = () => {
  const { setLoading, setLoadingMessage } = useLoader();
  const user = AuthService.getInstance().getCurrentUser();
  const { currentPlan } = useAccess();

  const [organizationDetails, setOrganizationDetails] =
    useState<Organization | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTabKey, setActiveTabKey] = useState<string>(
    searchParams.get("tab") || "general",
  );

  const isAdmin = user && user.role === "admin";
  const isIntegrationEnabled =
    currentPlan && currentPlan.limits.integration && isAdmin;

  const handleTabChange = (key: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("subTab");
      newParams.set("tab", key);
      return newParams;
    });
    setActiveTabKey(key);
  };

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab") || "general";
    if (tabFromUrl !== activeTabKey) {
      setActiveTabKey(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setLoadingMessage("Loading organization details...");
    try {
      const data = await organizationService.getOrganizationData();
      setOrganizationDetails(data);
    } catch (error) {
      console.error("Error fetching organization details:", error);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const renderDetails = (organizationDetails: Organization) => (
    <OrgDetails
      organizationDetails={organizationDetails}
      setOrganizationDetails={setOrganizationDetails}
    />
  );

  const renderThemes = (organizationDetails: Organization) => (
    <ThemeDetails
      organizationDetails={organizationDetails}
      setOrganizationDetails={setOrganizationDetails}
    />
  );

  const renderIntegration = (organizationDetails: Organization) => (
    <IntegrationDetails organizationDetails={organizationDetails} />
  );

  const renderApps = (organizationDetails: Organization) => (
    <Apps organizationDetails={organizationDetails} />
  );

  const items = [
    {
      key: "general",
      label: "General",
    },
    {
      key: "theme",
      label: "Theme",
    },
  ];

  if (isIntegrationEnabled && isAdmin) {
    items.push({
      key: "integration",
      label: "Integration",
    });
  }

  if (isAdmin) {
    items.push({
      key: "apps",
      label: "Apps",
    });
  }

  const renderActiveTabChildren = () => {
    if (!organizationDetails) return null;
    if (activeTabKey === "general") {
      return renderDetails(organizationDetails);
    } else if (activeTabKey === "theme") {
      return renderThemes(organizationDetails);
    } else if (activeTabKey === "integration") {
      return renderIntegration(organizationDetails);
    } else if (activeTabKey === "apps") {
      return renderApps(organizationDetails);
    }
  };

  return (
    <section className="organization-settings-page hide-scroll-bar">
      <OrganizationSettingsPageTitle />
      <Tabs
        style={{ margin: "0px", width: "100%" }}
        activeKey={activeTabKey}
        items={items}
        onChange={handleTabChange}
        size="small"
      />
      <div className="organization-settings-page-tab-children-wrapper hide-scroll-bar">
        {renderActiveTabChildren()}
      </div>
    </section>
  );
};

export default OrganizationSettings;
